import { Rng } from '../core/rng';
import { clamp, Vec3 } from '../core/math';
import { CANNONS, type CannonSpec } from '../data/ship';
import { AMMO, type AmmoId, type ShipClass } from '../data/classes';
import type { SimEvents } from './events';
import type { Ship } from './ship';
import type { Ballistics } from './ballistics';
import type { GunCrewSlot } from './crew';
import type { Wind } from './wind';
import { FLIGHT_DT, makeLay, muzzleDesign, muzzleSpeed, shotDrag, solveLay, type Lay } from './gunnery';

// The great guns of one ship. Guns only reload while men serve them; a
// reload takes around twenty seconds for a full, practised crew and far
// longer short-handed. Each gun fires along its own axis, so the ship's roll
// at the moment of firing throws the shot high or low - timing the roll is
// the gun captain's art.

const RELOAD = 22;

export interface Gun extends GunCrewSlot {
  /** Index into CANNONS. */
  index: number;
  hp: number;
  loaded: boolean;
  /** Reload progress 0..1. */
  reload: number;
  /** What's rammed home. */
  charge: AmmoId;
  recoil: number;
  sinceFired: number;
  /** Pending shot time (sim seconds) or -1. */
  fireAt: number;
  /** Quoin setting above the deck plane, rad. */
  elevation: number;
  /** Trained forward (+) or aft of the beam with handspikes, rad. */
  train: number;
  /** Where her crew are heaving her while she's laid on a mark (NaN: left as she is). */
  wantTrain: number;
  wantElevation: number;
  /** Why she can't be laid on the mark, or 'ok' (see AimStatus). */
  bears: AimStatus;
  /** Trained and quoined onto the mark (for her steady heel), to within a small part of the fall of shot there. */
  laid: boolean;
  /** Laid, and the roll of the moment brings her bore onto the mark: her captain fires now. */
  onMark: boolean;
  /** Her laying solution in the world (warm start for the next step). */
  lay: Lay;
  /** Sim time by which her captain fires anyway, laid or not. */
  fireBy: number;
}

/** How far a carriage gun can be trained off the beam. */
export const TRAIN_LIMIT = 0.26;
/** Quoin limits: how far the breech can be raised or lowered (rad above the deck plane). */
export const ELEVATION_MIN = -0.08;
export const ELEVATION_MAX = 0.14;
/** How fast a full crew heaves a gun round with handspikes, and raises or lowers it, rad/s. */
const TRAIN_RATE = 0.12;
const ELEVATION_RATE = 0.1;
/** A gun captain waits this long at most for his gun (and the roll) to come onto the mark. */
const LAY_WAIT = 3;

/**
 * Whether a gun can be laid on the mark: 'ok', or beyond her port's traverse
 * ('arc'), beyond the quoin's reach ('range'), too close to depress onto
 * ('close'), or within reach of an upright gun but not with the ship heeled
 * as she is ('heel': the weather guns of a ship heeled hard over can't be
 * depressed far enough, nor the lee guns raised).
 */
export type AimStatus = 'ok' | 'arc' | 'range' | 'close' | 'heel';

/** A side laid on a mark (the player's aim), for the HUD and the view. */
export interface BatteryAim {
  side: 1 | -1;
  /** The mark, world (at sea level), and how high over it the guns are laid (0: on the sea), m. */
  x: number;
  z: number;
  height: number;
  /** From the middle of the battery, m. */
  range: number;
  /** Time of flight there, s. */
  tof: number;
  /** Radius about the mark that should take most of a broadside (two sigma of the fall of shot there), m. */
  spread: number;
  /** Serviceable guns on the side; those that can be laid on the mark; those on it now; those loaded. */
  guns: number;
  bearing: number;
  laid: number;
  loaded: number;
  /** Guns laid, their captains waiting for the roll to bring them onto the mark. */
  roll: number;
  /** 'ok' if any gun bears; otherwise why none does. */
  status: AimStatus;
}

export class Battery {
  readonly guns: Gun[];
  /** Ammunition the crews load next. */
  ammo: AmmoId = 'round';
  readonly weight: number;
  private time = 0;
  /** Crew skill 0..1 (from the crew each step). */
  gunnery = 0.6;
  /** How unsteady the gun captains' hands are (1 sober; grog makes it worse). */
  unsteady = 1;
  /** A side being laid on a mark (the player's aim), or null. */
  aim: BatteryAim | null = null;
  /** Her steady heel, how much she rolls about it and how fast (rad, rad^2, (rad/s)^2), for laying and the spread. */
  private heelMean = 0;
  private rollVar = 0;
  private rollRateVar = 0;

  constructor(readonly cls: ShipClass, private readonly rng: Rng) {
    this.weight = cls.gunWeight;
    this.guns = cls.guns.map((index) => ({
      index, spec: CANNONS[index]!, destroyed: false, crew: 0, hp: 30 * Math.cbrt(cls.gunWeight),
      loaded: true, reload: 1, charge: 'round' as AmmoId, recoil: 0, sinceFired: 99, fireAt: -1, elevation: 0.02, train: 0,
      wantTrain: NaN, wantElevation: NaN, bears: 'ok' as AimStatus, laid: false, onMark: false, lay: makeLay(), fireBy: 0,
    }));
  }

  /** Guns on a side loaded and able to fire. */
  ready(side: 1 | -1): number {
    let n = 0;
    for (const g of this.guns) if (g.spec.side === side && g.loaded && !g.destroyed && g.fireAt < 0) n++;
    return n;
  }

  count(side: 1 | -1): number {
    let n = 0;
    for (const g of this.guns) if (g.spec.side === side && !g.destroyed) n++;
    return n;
  }

  /** Mean reload progress of a side's serviceable guns. */
  reloadProgress(side: 1 | -1): number {
    let p = 0, n = 0;
    for (const g of this.guns) {
      if (g.spec.side !== side || g.destroyed) continue;
      p += g.loaded ? 1 : g.reload;
      n++;
    }
    return n > 0 ? p / n : 0;
  }

  setElevation(side: 1 | -1, elevation: number): void {
    for (const g of this.guns) if (g.spec.side === side) g.elevation = clamp(elevation, ELEVATION_MIN, ELEVATION_MAX);
  }

  /** Train a side's guns forward (+) or aft (-) of the beam. */
  setTrain(side: 1 | -1, angle: number): void {
    for (const g of this.guns) if (g.spec.side === side) g.train = clamp(angle, -TRAIN_LIMIT, TRAIN_LIMIT);
  }

  /** Fire a ripple broadside, bow to stern. Returns guns fired. */
  fire(side: 1 | -1): number {
    const guns = this.guns
      .filter((g) => g.spec.side === side && g.loaded && !g.destroyed && g.fireAt < 0 && g.crew > 0.2)
      .sort((a, b) => b.spec.x - a.spec.x);
    let t = this.time + 0.05;
    for (const g of guns) {
      g.fireAt = t;
      g.fireBy = t + LAY_WAIT;
      t += 0.16 + this.rng.range(0, 0.12);
    }
    return guns.length;
  }

  /**
   * Lay a side's guns on a point of the sea (world x, z), or `height` metres
   * over it (a ship's hull or rigging there), shot flown at `flightDt` (the
   * step Ballistics flies it at, whatever step this ship is kept at). Each gun is solved for on its own, so the
   * broadside converges on the mark, and heaved toward its solution only as
   * fast as the men at it can manage - never past her port's traverse or the
   * quoin's reach. The quoin is set for her steady heel: nobody chases the
   * roll with it; the gun captain fires as the roll brings the bore on. The
   * other side's guns are left as they are.
   */
  layOn(side: 1 | -1, x: number, z: number, dt: number, ship: Ship, wind: Wind, height = 0, flightDt = FLIGHT_DT): BatteryAim {
    const heel = ship.heel;
    const roll = heel - this.heelMean;
    const rollRate = Math.abs(ship.angVelLocal.x);
    const windX = wind.dirX * wind.speed, windZ = wind.dirZ * wind.speed;
    const aim = (this.aim ??= { side, x, z, height, range: 0, tof: 0, spread: 0, guns: 0, bearing: 0, laid: 0, loaded: 0, roll: 0, status: 'ok' });
    aim.side = side;
    aim.x = x;
    aim.z = z;
    aim.height = height;
    let n = 0, bearing = 0, laid = 0, loaded = 0, rolled = 0, tof = 0, range = 0, drop = 0;
    const why: Record<AimStatus, number> = { ok: 0, arc: 0, range: 0, close: 0, heel: 0 };
    for (const g of this.guns) {
      if (g.spec.side !== side) {
        g.wantTrain = g.wantElevation = NaN;
        g.laid = g.onMark = false;
        continue;
      }
      if (g.destroyed) continue;
      n++;
      if (g.loaded) loaded++;
      const charge = g.loaded ? g.charge : this.ammo;
      const speed = muzzleSpeed(g.spec, charge);
      const muzzle = ship.designToWorld(muzzleDesign(g.spec, tmpA), tmpM);
      const fresh = !Number.isFinite(g.lay.miss);
      solveLay(g.lay, muzzle, ship.vel, speed, shotDrag(charge, this.weight), x, z, windX, windZ, flightDt, fresh ? 8 : 2, 0.6, height);
      const R = Math.hypot(x - muzzle.x, z - muzzle.z);
      range += R;
      tof += g.lay.t;
      drop += 0.5 * 9.81 * g.lay.t * g.lay.t;
      // The bore in the ship's frame, where the gun is trained and quoined:
      // along (tan train, elevation, side).
      const ce = Math.cos(g.lay.el);
      const d = ship.rot.rotateInverse(tmpD.set(Math.cos(g.lay.az) * ce, Math.sin(g.lay.el), Math.sin(g.lay.az) * ce));
      const out = d.z * side;
      let status: AimStatus = 'ok';
      let train: number, elev: number;
      if (out > 0.05) {
        train = Math.atan2(d.x, out);
        elev = d.y / out;
      } else {
        // Abaft the beam line or across the ship: hard over, as near as she'll come.
        train = Math.abs(d.x) > 0.05 ? Math.sign(d.x) * Math.PI / 2 : g.train;
        elev = Math.tan(g.lay.el) + side * heel;
      }
      if (out <= 0.05 || Math.abs(train) > TRAIN_LIMIT + 1e-4) status = 'arc';
      // The quoin an upright gun would want, and one on her steady heel.
      const upright = Math.tan(g.lay.el) / Math.cos(clamp(train, -TRAIN_LIMIT, TRAIN_LIMIT));
      const steady = upright + side * this.heelMean;
      if (status === 'ok') {
        if (upright > ELEVATION_MAX + 1e-4) status = 'range';
        else if (upright < ELEVATION_MIN - 1e-4) status = 'close';
        else if (steady > ELEVATION_MAX + 1e-4 || steady < ELEVATION_MIN - 1e-4) status = 'heel';
      }
      g.bears = status;
      why[status]++;
      if (status === 'ok') bearing++;
      g.wantTrain = clamp(train, -TRAIN_LIMIT, TRAIN_LIMIT);
      g.wantElevation = clamp(elev - side * roll, ELEVATION_MIN, ELEVATION_MAX);
      // Handspikes and quoins, as fast as the men at the gun can work them
      // (not while she's still recoiling).
      const need = g.spec.deck === 'main' ? 3 : 2;
      const pace = g.crew > 0.2 && g.sinceFired > 1.2 ? Math.pow(Math.min(1, g.crew / need), 0.7) * dt : 0;
      g.train += clamp(g.wantTrain - g.train, -TRAIN_RATE * pace, TRAIN_RATE * pace);
      g.elevation += clamp(g.wantElevation - g.elevation, -ELEVATION_RATE * pace, ELEVATION_RATE * pace);
      // Laid: lay errors small beside the fall of shot there. On the mark:
      // the roll of the moment brings the bore onto it too (it passes a
      // step's worth of roll each step, so that much is near enough).
      const tol = Math.max(0.6 / Math.max(R, 1), 0.35 * this.angularSpread(charge));
      g.laid = status === 'ok' && Math.abs(g.train - g.wantTrain) < tol && Math.abs(g.elevation - g.wantElevation) < tol;
      g.onMark = g.laid && Math.abs(g.elevation - elev) < Math.max(tol, 0.75 * rollRate * dt);
      if (g.laid) laid++;
      if (g.laid && !g.onMark) rolled++;
    }
    const m = Math.max(1, n);
    aim.guns = n;
    aim.bearing = bearing;
    aim.laid = laid;
    aim.loaded = loaded;
    aim.roll = rolled;
    aim.range = range / m;
    aim.tof = tof / m;
    // None bears: the nearest thing to it (guns within the arc but out of reach say more than those without).
    aim.status = bearing > 0 || n === 0 ? 'ok' : why.close ? 'close' : why.range ? 'range' : why.heel ? 'heel' : 'arc';
    aim.spread = this.spreadAt(aim.range, drop / m, this.ammo);
    return aim;
  }

  /** Her steady heel, rad: the roll averaged out over several seconds (a gun fired on the roll fires at it). */
  get steadyHeel(): number {
    return this.heelMean;
  }

  /** Leave off laying the guns: they stay as they were. */
  standDown(): void {
    if (!this.aim) return;
    this.aim = null;
    for (const g of this.guns) {
      g.wantTrain = g.wantElevation = NaN;
      g.laid = g.onMark = false;
      g.lay.miss = NaN;
    }
  }

  /** The laying shake of her gun captains, rad (one sigma): practised crews keep it small, drink makes it worse. */
  get shake(): number {
    return (0.004 + (1 - this.gunnery) * 0.009) * this.unsteady;
  }

  /** The laying shake of her gun captains and a case's spread, rad (one sigma, across the line). */
  private angularSpread(ammo: AmmoId): number {
    return Math.hypot(this.shake, AMMO[ammo].spread);
  }

  /**
   * Radius about a mark `range` metres off that should take most of a
   * broadside (two sigma): the gun captains' shake and a case's spread, how
   * well they catch the roll, and the powder's variation (a slower ball
   * drops more over the same run).
   */
  spreadAt(range: number, drop: number, ammo: AmmoId): number {
    const a = AMMO[ammo];
    const shake = this.shake;
    const across = Math.hypot(shake, a.spread);
    const roll = 0.03 * Math.sqrt(this.rollRateVar) + 0.05 * Math.sqrt(this.rollVar);
    const powder = (2 * drop * 0.0173) / Math.max(range, 1);
    const up = Math.hypot(shake * 0.8, a.spread * 0.7, roll, powder);
    return Math.max(1.5, 2 * range * Math.sqrt((across * across + up * up) / 2));
  }

  /** Fire one gun as it bears. */
  fireGun(i: number): boolean {
    const g = this.guns[i];
    if (!g || !g.loaded || g.destroyed || g.fireAt >= 0 || g.crew <= 0.2) return false;
    g.fireAt = this.time + this.rng.range(0, 0.25);
    g.fireBy = g.fireAt + LAY_WAIT;
    return true;
  }

  damageGun(i: number, amount: number): boolean {
    const g = this.guns[i];
    if (!g || g.destroyed) return false;
    g.hp -= amount;
    if (g.hp <= 0) {
      g.destroyed = true;
      g.loaded = false;
      return true;
    }
    return false;
  }

  update(dt: number, ship: Ship, ballistics: Ballistics, events: SimEvents, vessel: number): void {
    this.time += dt;
    // Her roll about her steady heel, over the last several seconds.
    const a = 1 - Math.exp(-dt / 6);
    this.heelMean += (ship.heel - this.heelMean) * a;
    const roll = ship.heel - this.heelMean, rate = ship.angVelLocal.x;
    this.rollVar += (roll * roll - this.rollVar) * a;
    this.rollRateVar += (rate * rate - this.rollRateVar) * a;
    for (let i = 0; i < this.guns.length; i++) {
      const g = this.guns[i]!;
      g.sinceFired += dt;
      if (g.fireAt >= 0 && this.time >= g.fireAt) {
        if (this.aim && g.spec.side === this.aim.side && g.bears === 'ok' && !g.onMark && this.time < g.fireBy) {
          // Laid on a mark but not on it yet: her captain waits for the
          // handspikes, and the roll, to bring her on.
          g.fireAt = this.time + dt;
        } else {
          g.fireAt = -1;
          if (g.loaded && !g.destroyed && g.crew > 0.2) this.shoot(i, g, ship, ballistics, events, vessel);
        }
      }
      // Sponge, load, ram and run out: only with hands on the tackles.
      if (!g.loaded && !g.destroyed && g.sinceFired > 1.5) {
        const need = g.spec.deck === 'main' ? 3 : 2;
        const hands = clamp(g.crew / need, 0, 1.15);
        const size = g.spec.deck === 'main' ? 1 : 0.75;
        const t = RELOAD * size * AMMO[this.ammo].reload * Math.sqrt(this.weight) / (0.55 + 0.45 * this.gunnery);
        g.reload += (dt * Math.pow(hands, 0.8)) / t;
        if (g.reload >= 1) {
          g.loaded = true;
          g.reload = 1;
          g.charge = this.ammo;
        }
      }
      // Carriage recoil and running out again.
      const s = g.sinceFired;
      g.recoil = s < 0.12 ? s / 0.12 : g.loaded ? Math.max(0, g.recoil - dt / 3) : 1;
    }
  }

  private shoot(index: number, g: Gun, ship: Ship, ballistics: Ballistics, events: SimEvents, vessel: number): void {
    const ammo = AMMO[g.charge];
    const muzzle = ship.designToWorld(muzzleDesign(g.spec, tmpA), new Vec3());
    // Aim: the quoin elevation plus a shake that practised crews keep small.
    const spread = this.shake;
    const dir = tmpB.set(Math.tan(g.train) + this.rng.gauss() * spread, g.elevation + this.rng.gauss() * spread * 0.8, g.spec.side).normalize();
    ship.rot.rotate(dir);
    const mv = muzzleSpeed(g.spec, g.charge);
    for (let k = 0; k < ammo.count; k++) {
      const d = tmpC.copy(dir);
      if (ammo.spread > 0) {
        d.x += this.rng.gauss() * ammo.spread;
        d.y += this.rng.gauss() * ammo.spread * 0.7;
        d.z += this.rng.gauss() * ammo.spread;
        d.normalize();
      }
      const vel = new Vec3().copy(d).scale(mv * this.rng.range(0.97, 1.03)).add(ship.vel);
      ballistics.launch(vessel, muzzle, vel, g.charge, this.weight);
    }
    g.loaded = false;
    g.reload = 0;
    g.sinceFired = 0;
    events.push({ type: 'cannonFire', index, x: muzzle.x, y: muzzle.y, z: muzzle.z, dx: dir.x, dy: dir.y, dz: dir.z, vessel, weight: this.weight });
    // Shot momentum plus powder gases.
    const impulse = ammo.mass * ammo.count * this.weight * mv * 1.6 * (g.spec.deck === 'main' ? 1 : 0.5);
    ship.applyImpulse(-dir.x * impulse, -dir.y * impulse, -dir.z * impulse, muzzle.x, muzzle.y, muzzle.z);
  }
}

/** A gun's bore direction in the world as she's laid now (no shake). */
export function boreDirection(g: Gun, ship: Ship, out: Vec3): Vec3 {
  return ship.rot.rotate(out.set(Math.tan(g.train), g.elevation, g.spec.side).normalize());
}

const tmpA = new Vec3();
const tmpB = new Vec3();
const tmpC = new Vec3();
const tmpD = new Vec3();
const tmpM = new Vec3();

export type { CannonSpec };
